// diag/readyChainLog.js — el registro del VISOR de la cadena de entrega.
//
// Portado tal cual desde diag/f66ap-ready-chain (donde nació como
// instrumentación temporal del diagnóstico D-U). En F6.7a deja de ser temporal
// y se queda como el INSTRUMENTO del sprint: la tubería nueva (convergencia,
// coalescing, backoff, vigía de red) corre en momentos que jsdom no puede
// simular —lifecycle real de iOS, señal de red que va y viene, la app matada y
// relanzada— y la única forma honesta de validarla es leerla en el teléfono.
//
// CONTRATOS DURÍSIMOS (heredados del diagnóstico, intactos):
//   - APP-ONLY: todo gateado por IS_APP_BUILD. En web CADA función es un no-op
//     total (return temprano) → el comportamiento web queda byte-idéntico.
//   - JAMÁS ALTERA LA CADENA: log() es fire-and-forget. Nunca `await`, nunca
//     lanza (try/catch interno que se traga todo), nunca cambia orden ni
//     tiempos. Escribir en memoria es síncrono y barato; la persistencia va con
//     throttle FUERA del camino crítico (setTimeout, no bloquea el render).
//   - SOBREVIVE AL KILL: cada entrada lleva un bootId (aleatorio por arranque);
//     el buffer se persiste en localStorage (un solo registro) tipo ring, tope
//     500 entradas, para comparar la corrida en vivo contra el arranque
//     siguiente.
//   - LECTOR RESILIENTE: una entrada corrupta no tumba el visor (lección F6.1
//     "un juez que truena no es juez"): getEntries hace try/catch por entrada y
//     cuenta las ilegibles.
//
// Cada entrada: { b: bootId, w: wallClock ISO, m: monotónico ms, c: código, d: detalle }.
//
// Códigos de la tubería F6.7a (los del diagnóstico siguen vivos):
//   CV.RUN    — la convergencia corrió: motivo + huecos encontrados.
//   CV.SKIP   — corrió y no había nada que hacer (noop) o no se pudo leer.
//   CV.ABORT  — la época de identidad cambió: la corrida se descarta sin escribir.
//   DL.RETRY  — reintento automático tras un fallo de red (intento n, backoff).
//   NET.UP / NET.DOWN — el vigía de red vio la señal irse o volver.
//
// Códigos añadidos en la 2ª pasada de F6.7b (los dos nacen de mediciones que
// el smoke del 28-jul NO pudo hacer por falta de instrumento):
//   DL.DIFF   — por qué se (re)descarga un doc: {id, why:'ausente'|'hash'|'rev',
//               rev:[local, remoto], hash:[local, remoto]}. Si un doc vuelve a
//               bajar en ciclo, la primera línea del registro dice el campo
//               culpable — se acabó el adivinar.
//   SCR.PAINT — una pantalla recibió DATO FRESCO del servidor ({screen}). Con
//               NET.UP arriba, "cuánto tarda la app en volver a la verdad viva
//               tras recuperar la señal" se mide restando dos marcas de tiempo.
//
// Códigos añadidos en PEND-1 (la pantalla de Pendientes era MUDA en el visor:
// ni ella ni su hook emitían una sola línea, así que un smoke no podía saber si
// la pantalla se quedó en blanco por falta de semilla, por el TTL o por la red).
// Los emite `usePendingSummary`, o sea que cubren también los dos badges:
//   SCR.EMPTY — una pantalla MONTÓ sin nada del servidor que pintar
//               ({screen, motivo, edadSemillaMs, teniaSemilla}). `motivo` vale
//               'sin-semilla' (el cache no tenía entrada) o 'semilla-vencida'
//               (la tenía, pasó el TTL y hay señal, así que se va al servidor).
//               `edadSemillaMs` es null cuando no hay semilla que medir. Es la
//               contraparte de SCR.PAINT: dice POR QUÉ no se pintó nada.
//   SCR.STALE — una pantalla pintó su ÚLTIMA FOTO, ya vencida, porque el
//               veredicto dictamina que no hay red ({screen, grupos,
//               edadSemillaMs}). Hermana de SCR.FALLBACK/SCR.RECOVER: como
//               ellas, declara que lo pintado NO es dato fresco del servidor —
//               por eso NO usa SCR.PAINT, cuyo contrato de arriba es justo el
//               contrario. Lo estrena la excepción D1 de `usePendingSummary`.
//
// Códigos de COS-G:
//   MONO.RECAPTURE — el par reloj-de-pared ↔ reloj-monótono del PROCESO se
//               volvió a capturar al regresar a primer plano ({sitio, pared,
//               mono}: cuánto se movió cada uno respecto del ancla anterior).
//               Lo emite `sessionRuntime.captureClockAnchor`.
//               SE LLAMABA `CLOCK.REANCHOR` y era confundible con
//               `EXAM.CLOCK.REANCHOR` (examRuntime.js), que re-ancla el DEADLINE
//               de un examen concreto y NO cambió. Éste no sabe nada de
//               exámenes: es el reloj del proceso, y por eso ya no comparte
//               ninguna palabra con aquél.
//   FLASH.TOUCH — COS-33, INSTRUMENTACIÓN PURA (cero fix). Un `touchstart`
//               aterrizó en la flashcard ({volteada, caraTocada, targetTag}).
//               Lo emite el ENVOLTORIO de la tarjeta (`Flashcards.js`), no el
//               listener de scroll de las caras: ése se engancha con deps
//               `[currentIdx, flipped]` y en la primera tarjeta de la sesión no
//               llega a recibir el toque — ahí la línea habría sido muda justo
//               en el caso que hay que medir.
//               `volteada` es el estado de React (¿se está viendo
//               el reverso?); `caraTocada` es a qué cara pertenece el target
//               REAL del evento, resuelto con
//               `closest('.flashcard-front, .flashcard-back')` — 'front',
//               'back' o 'ninguna'. Existe para separar dos hipótesis del bug
//               "el frente no scrollea con el dedo en iPhone y el reverso sí":
//               si con `volteada:false` el toque cae en `caraTocada:'back'`, el
//               reverso (último hermano, sin z-index) se está comiendo el
//               hit-testing; si cae en 'front', la causa es otra (el
//               preserve-3d documentado en index.css:145-154).
//
// Código de la TANDA J (COS-33 v2 — el censo cara-contra-cara cerró SIN
// asimetría de código: las dos caras son idénticas en todo lo que gobierna el
// scroll táctil, y la única diferencia declarada —el valor del `transform`— ya
// la desmintió el iPhone en el smoke M-2. El dato que falta sólo lo da el
// aparato, y éste es el instrumento que lo produce):
//   FLASH.SCROLL — UN INTENTO DE SCROLL sobre una cara de la flashcard SE
//               SOLTÓ. Una línea por GESTO (no por frame): se arma en el
//               `touchstart`, acumula en los `touchmove` y se escribe al soltar
//               (`touchend`/`touchcancel`), que es el único momento en que se
//               puede decir si el scroller DE VERDAD se movió. Un tap no escribe
//               nada: hacen falta ≥ 6 px de recorrido de dedo.
//               La emite `Flashcards.js` desde el MISMO efecto que engancha el
//               handler de scroll de las caras, con listeners registrados
//               DESPUÉS de los de producción y `passive: true` — no pueden
//               cancelar nada, y por ir después leen `e.defaultPrevented` ya
//               decidido. El handler de producción queda byte a byte igual.
//               CAMPOS:
//                 cara        'frente' | 'reverso'. Es la cara CUYO listener
//                     recibió el toque, o sea la que el navegador eligió en el
//                     hit-testing — el dato que separa las dos hipótesis.
//                     (Gemelo en español de `caraTocada` de FLASH.TOUCH, que
//                     conserva 'front'/'back' porque cinco rojos lo leen así.)
//                 volteada    el estado de React: ¿se está viendo el reverso?
//                 deltaY      recorrido del DEDO, redondeado, con el MISMO signo
//                     que el handler de producción: positivo = el dedo subió
//                     (el contenido debería bajar).
//                 prevented   ¿alguien canceló el `touchmove` en el camino hasta
//                     esta cara (incluido el handler de producción)?
//                 scrollTop   el de la cara AL SOLTAR.
//                 scrollTop0  el de la cara AL EMPEZAR, ya con el empujón de iOS
//                     aplicado (el handler de producción corre antes).
//                 hayDesborde ¿la cara tenía algo que scrollear al empezar?
//                 alto        [scrollHeight, clientHeight] al empezar.
//                 cancelable  ¿el último `touchmove` llegó cancelable? En falso,
//                     iOS ya se había quedado con el gesto (WebKit #163207).
//               CÓMO SE LEE (el dedo de Carlos, arrastrando el FRENTE sin
//               voltear, con una tarjeta larga):
//                 · {cara:'reverso', volteada:false} → el reverso se come el
//                   hit-testing: el dedo nunca fue del frente.
//                 · {cara:'frente', hayDesborde:true, scrollTop0 === scrollTop}
//                   → el dedo sí es del frente y el scroller NUNCA enganchó.
//                 · {cara:'frente', hayDesborde:false} → el frente ni siquiera
//                   desborda: no hay nada que scrollear.
//                 · {prevented:true} → lo cancela este mismo código.
//               Y el contraste obligatorio: el mismo arrastre sobre el REVERSO
//               (que sí scrollea) debe dar {cara:'reverso', hayDesborde:true} con
//               `scrollTop` distinto de `scrollTop0`. Ésa es la línea "sana"
//               contra la que se compara la enferma.
//
// Códigos de COS-H:
//   NAV.SWIPEBACK — el gesto de salida estilo iOS SE SOLTÓ ({pantalla, commit}).
//               Lo emite `lib/useEdgeSwipeBack.js`, una sola vez por gesto y
//               siempre al soltar: con `commit:true` la pantalla ejecutó el
//               mismo handler que su botón de salir; con `commit:false` la vista
//               volvió con resorte y NO pasó nada (no llegó al umbral, o el
//               sistema/otro dedo se llevó el gesto). `pantalla` vale
//               'adaptive', 'adaptive-summary', 'flashcards',
//               'flashcards-summary' o 'exam-results' — el examen EN CURSO no
//               está en esa lista a propósito (COS-40: ahí no hay gesto).
//               COS-H-b añade las dos pantallas hermanas que el HUECO 4 dejó
//               fuera, con el MISMO hook y las mismas reglas:
//                 'adaptive-no-questions' — la de "¡Respondiste todas las
//                     preguntas!" de adaptiva; su gesto es el botón "Ver
//                     Resultados" (`endSession`), no una salida al panel.
//                 'flashcards-mastered'   — la de "¡Dominaste las N flashcards!";
//                     su gesto es "Salir al Panel".
//               COS-H-b2 añade la última de la serie:
//                 'flashcards-empty'      — la de "No hay flashcards
//                     disponibles"; su gesto es "Volver al Panel", que es LA
//                     MISMA función que usa 'flashcards-mastered'. Si esta
//                     línea aparece en un registro real, es noticia: hoy
//                     ninguna puerta del producto deja llegar a esa pantalla
//                     (el servidor responde 400 con el mazo vacío, el motor
//                     local lanza, el Study Center no deja arrancar y borrar la
//                     última tarjeta está prohibido en las dos capas).
//               CADUCA (pantallas-alumno, AD6 y FC11): 'adaptive-no-questions'
//               y 'flashcards-empty' ya no existen; sus pantallas se quitaron
//               (no se alcanzaban) y esas dos líneas no pueden volver a salir.
//               Un toque que nace fuera de la franja del borde, o que se queda
//               por debajo del umbral de dirección, no es un gesto y no escribe
//               línea: el registro cuenta gestos soltados, no dedos apoyados.
//               El cambio de tarjeta de COS-38 (`useCardSwipe`) NO escribe aquí:
//               no es una salida, y su efecto ya se ve en la pantalla.
//
// Código de REMATE-B (el gesto "agarraba, pero sólo en cierto lugar"):
//   NAV.SWIPEBACK.MISS — un dedo APUNTÓ AL BORDE y NO terminó en commit
//               ({pantalla, xInicial, y, motivo}). Hermano de la línea de
//               arriba, no la misma: aquélla dice "un gesto se soltó" y ésta
//               dice DÓNDE nació el intento y POR QUÉ murió, que es justo lo
//               que el registro no sabía contestar cuando el feel se siente
//               mal. `xInicial` e `y` son las coordenadas del `touchstart`, en
//               píxeles de pantalla y redondeadas.
//               Sólo se escribe si el toque nació dentro de la franja de
//               VIGILANCIA (`WATCH_ZONE_PX`, 60 px desde el filo izquierdo): un
//               toque en el centro no es un intento de salir.
//               `motivo` vale, y son los cinco que el código distingue de
//               verdad (`MISS_REASONS` en lib/useEdgeSwipeBack.js):
//                 'fuera-de-zona'       nació PASADA la franja del gesto
//                     (EDGE_ZONE_PX) pero dentro de la vigilada. Es EL dato de
//                     calibración: si los fallos se amontonan aquí, la franja
//                     quedó corta y hay número que subir.
//                 'apagado'             nació dentro de la franja con el gesto
//                     deshabilitado — había una capa encima (modal, prompt,
//                     onboarding) y el dedo era de la capa.
//                 'capturado-por-otro'  el candado de dirección lo soltó: el
//                     movimiento fue VERTICAL y el dedo se le dejó al scroll.
//                 'umbral-no-alcanzado' se soltó sin llegar. Es el único motivo
//                     que puede venir ACOMPAÑADO de un `NAV.SWIPEBACK` con
//                     `commit:false` (el resorte): esa pareja de líneas es un
//                     arrastre que se quedó corto. Solo, sin pareja, es un dedo
//                     que se apoyó en el borde y se levantó.
//                 'interrumpido'        otro dedo (pinch) o un `touchcancel`
//                     del sistema se llevaron el intento.
//               Escucha PASIVA y app-only como todo el visor: no cancela nada,
//               no marca el evento y el gesto jamás lee lo que aquí se escribe.
//               `useCardSwipe` tampoco escribe esta línea.
//
// Códigos de COS-I:
//   NAV.TABSWIPE — el swipe horizontal entre las pestañas de la píldora SE
//               SOLTÓ ({desde, hacia, commit}). Lo emite `lib/useTabSwipe.js`,
//               una sola vez por gesto y siempre al soltar. `desde` y `hacia`
//               son RUTAS ('/dashboard', '/study', '/analytics', '/upgrade'),
//               no nombres de pantalla: es el mismo vocabulario con el que la
//               píldora nombra sus destinos, y así la línea se lee contra el
//               orden de tabs sin traducir nada.
//                 · commit:true  → se disparó el tap de la píldora sobre
//                   `hacia`, con su tic háptico. Todo lo que un tap en ese tab
//                   provoca, lo provocó el dedo — incluido el candado de sesión.
//                 · commit:false → no pasó nada. Dos causas, y `hacia` las
//                   separa: con RUTA, el recorrido no llegó al umbral (resorte)
//                   o el sistema/otro dedo se llevó el gesto; con `hacia:null`,
//                   el gesto fue hacia un EXTREMO —de Inicio hacia la derecha o
//                   de Mejorar plan hacia la izquierda— y ahí no hay vecina
//                   porque el orden no da la vuelta.
//               'More' no aparece jamás en `hacia`: es un cajón, no una
//               pestaña, y el gesto no lo puede elegir. Tampoco escriben línea
//               las pantallas que NO montan el gesto (sesiones de estudio,
//               Pendientes, Study Log, Soporte) ni el DETALLE de Analíticas: ahí
//               no hay hook, así que el silencio en el registro es la prueba de
//               que el gesto no existe en esa superficie.
//               Un toque que se queda por debajo del umbral de dirección, o que
//               nace sobre una fuente excluida (los sliders del examen), no es
//               un gesto y no escribe línea: el registro cuenta gestos
//               soltados, no dedos apoyados.
//
// Código de CARRIL-DIAG (el corrimiento horizontal del smoke del build #72: el
// pane Inicio quedó corrido a la izquierda tras un gesto cancelado a medias, y
// cambiar de pestaña no lo arregló — sólo matar la app):
//   NAV.H1    — EL CARRIL VIOLÓ SU LEY DE REPOSO. Lo emite el tripwire
//               (`lib/diag/carrilTripwire.js`), un observador EXTERNO de solo
//               lectura: ni toca los archivos del carril ni corrige nada. Se
//               escribe SÓLO cuando el estado CAMBIA (una violación nueva, o una
//               que cambió de capa/propiedad/valor), porque su latido revisaría
//               la misma cada 15 s y en minutos se comería las 500 entradas del
//               anillo — que es justo el contexto que hace falta para leerla.
//               CAMPOS: `capa` (qué nodo: 'pane:/dashboard', 'carril', 'gesto',
//               'riel', 'main', 'documento', 'body'), `prop` ('scrollLeft' |
//               'transform' | 'left'), `valor`, `idx` (la pestaña activa según
//               el `left` del riel), `ruta`, `motivo` (qué disparó la revisión:
//               'estilo-riel' = cambio de pestaña, 'scrollend', 'gesto',
//               'latido', 'arranque') y `gesto`, que es el último NAV.TABSWIPE
//               del propio registro — la única forma de fechar la violación
//               contra un gesto sin tocar `useTabSwipe`.
//               El cierre del episodio se apunta con `{estado:'sano', motivo}`:
//               así en el registro se ve dónde empezó y dónde terminó.

import { IS_APP_BUILD } from '../buildTarget';

const STORAGE_KEY = 'sb_diag_ready_chain_v1';
const MAX_ENTRIES = 500;
const PERSIST_THROTTLE_MS = 1000;

// bootId nuevo por arranque del módulo (= por proceso de la app). Corto para
// que se lea de un vistazo en el teléfono.
function makeBootId() {
  try {
    return Math.random().toString(36).slice(2, 8);
  } catch (e) {
    return 'nobid';
  }
}

const BOOT_ID = makeBootId();

// Reloj monotónico (no salta con cambios de hora); base al importar el módulo.
function monoNow() {
  try {
    if (typeof performance !== 'undefined' && performance.now) return Math.round(performance.now());
  } catch (e) { /* noop */ }
  return 0;
}

function wallNow() {
  try {
    return new Date().toISOString();
  } catch (e) {
    return '';
  }
}

// ── Buffer en memoria, hidratado del disco al importar ───────────────────────
let buffer = [];

function hydrate() {
  if (!IS_APP_BUILD) return;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) buffer = parsed.slice(-MAX_ENTRIES);
  } catch (e) {
    // Registro ilegible / storage bloqueado → arrancamos con buffer vacío.
    buffer = [];
  }
}
hydrate();

// ── Persistencia con throttle (fuera del camino crítico) ─────────────────────
let persistTimer = null;

function schedulePersist() {
  if (persistTimer) return;
  try {
    persistTimer = setTimeout(() => {
      persistTimer = null;
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(buffer));
      } catch (e) { /* storage lleno/bloqueado: la evidencia en memoria sigue viva */ }
    }, PERSIST_THROTTLE_MS);
  } catch (e) {
    persistTimer = null;
  }
}

/**
 * log(code, detail) — apunta un eslabón de la cadena. Síncrono en memoria,
 * persistencia diferida. NUNCA lanza, NUNCA await. No-op en web.
 * @param {string} code  Código corto (CV.RUN, DL.RETRY, NET.UP, NL.POLL, …).
 * @param {object|string} [detail]  Datos legibles en el teléfono.
 */
export function log(code, detail) {
  if (!IS_APP_BUILD) return;
  try {
    buffer.push({ b: BOOT_ID, w: wallNow(), m: monoNow(), c: code, d: detail == null ? null : detail });
    if (buffer.length > MAX_ENTRIES) buffer.splice(0, buffer.length - MAX_ENTRIES);
    schedulePersist();
  } catch (e) {
    // Fire-and-forget: si el propio log falla, la cadena NO se entera.
  }
}

/**
 * getEntries() — lectura resiliente para el visor. Devuelve
 * { entries: [...], unreadable: n }. Cada entrada se valida por separado; una
 * corrupta se cuenta pero no rompe la lectura.
 */
export function getEntries() {
  const out = [];
  let unreadable = 0;
  for (const raw of buffer) {
    try {
      if (raw && typeof raw === 'object' && typeof raw.c === 'string') {
        out.push({
          b: String(raw.b ?? '?'),
          w: String(raw.w ?? ''),
          m: Number(raw.m ?? 0),
          c: raw.c,
          d: raw.d ?? null,
        });
      } else {
        unreadable += 1;
      }
    } catch (e) {
      unreadable += 1;
    }
  }
  return { entries: out, unreadable };
}

/** clear() — vacía el registro (memoria + disco). No-op en web. */
export function clear() {
  if (!IS_APP_BUILD) return;
  buffer = [];
  try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) { /* noop */ }
}

/** El bootId de ESTA corrida (para que el visor marque "corrida actual"). */
export function currentBootId() {
  return BOOT_ID;
}
